import { api } from "../data/api/client";

// Client half of web push (notifications.md §Web push). The dashboard registers
// the push service worker, subscribes via the browser PushManager using the
// server's VAPID public key, and hands the subscription to the server so the
// deliver_web_push job can reach this device.

// localStorage key holding the server-side subscription public_id, so we can
// revoke the exact row on unsubscribe. A miss is non-fatal — a dead endpoint is
// auto-revoked server-side on the next 404/410.
const SUBSCRIPTION_ID_KEY = "tin.webpush.subscriptionId";

export function isPushSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    "serviceWorker" in navigator &&
    typeof window !== "undefined" &&
    "PushManager" in window &&
    "Notification" in window
  );
}

// VAPID keys are base64url; the PushManager wants raw bytes. The explicit
// ArrayBuffer backing keeps the result assignable to BufferSource (a plain
// `Uint8Array` widens to ArrayBufferLike under TS's typed-array generics).
function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(normalized);
  const output = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
}

async function registerServiceWorker(): Promise<ServiceWorkerRegistration> {
  // The build compiles src/push/sw.ts to a stable /sw.js (see build.ts); register
  // it by that path with a root scope. (Registering via
  // `new URL('./sw.ts', import.meta.url)` breaks the production build — Vite
  // inlines the raw .ts as a data: URL, which browsers reject.)
  return navigator.serviceWorker.register("/sw.js");
}

// Fetch the server's VAPID public key; null when push is not configured
// server-side (no keypair), in which case the UI hides the toggle.
export async function fetchVapidPublicKey(): Promise<string | null> {
  const res = await api.get<{ public_key: string | null }>(
    "/web-push/vapid-public-key",
  );
  return res.public_key;
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!isPushSupported()) {
    return null;
  }
  // The dashboard registers exactly one service worker (the push SW), so the
  // first registration carrying a subscription is ours — no need to match the
  // hashed asset scope, which changes across builds.
  const registrations = await navigator.serviceWorker.getRegistrations();
  for (const registration of registrations) {
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      return subscription;
    }
  }
  return null;
}

export async function enablePush(vapidPublicKey: string): Promise<void> {
  if (!isPushSupported()) {
    throw new Error("Web push is not supported in this browser.");
  }
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("Notifications permission was denied.");
  }
  const reg = await registerServiceWorker();
  await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  const subscription =
    existing ??
    (await reg.pushManager.subscribe({
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
      userVisibleOnly: true,
    }));
  const json = subscription.toJSON();
  const created = await api.post<{ public_id: string }>(
    "/web-push-subscriptions",
    {
      endpoint: json.endpoint,
      keys: json.keys,
      user_agent: navigator.userAgent,
    },
  );
  window.localStorage.setItem(SUBSCRIPTION_ID_KEY, created.public_id);
}

export async function disablePush(): Promise<void> {
  const subscription = await currentSubscription();
  const storedId = window.localStorage.getItem(SUBSCRIPTION_ID_KEY);
  if (storedId) {
    await api.del(`/web-push-subscriptions/${storedId}`).catch(() => {
      // Best-effort: the endpoint is also auto-revoked on the next failed push.
    });
    window.localStorage.removeItem(SUBSCRIPTION_ID_KEY);
  }
  if (subscription) {
    await subscription.unsubscribe();
  }
}
